// ──────────────────────────────────────────────
// Axios instance with auth interceptors
// ──────────────────────────────────────────────

import axios, {
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiError } from '@/types/common';

/**
 * Resolve the API base URL at runtime based on the current hostname.
 * - app.worsaas.com → api.worsaas.com/api
 * - app.worsaas.es  → api.worsaas.es/api
 * - localhost / dev  → /api (Vite proxy)
 */
function resolveApiUrl(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.worsaas.com') return 'https://api.worsaas.com/api';
    if (host === 'app.worsaas.es') return 'https://api.worsaas.es/api';
  }
  return import.meta.env.VITE_API_URL ?? '/api';
}

const api = axios.create({
  baseURL: resolveApiUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ── Helpers to read / write persisted auth store ─────────────

const STORE_KEY = 'worsaas-auth';

interface PersistedAuth {
  state: {
    token: string | null;
    refreshToken: string | null;
  };
}

function getPersistedAuth(): PersistedAuth['state'] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { token: null, refreshToken: null };
    const parsed: PersistedAuth = JSON.parse(raw);
    return parsed.state;
  } catch {
    return { token: null, refreshToken: null };
  }
}

function clearPersistedAuth(): void {
  try {
    localStorage.removeItem(STORE_KEY);
  } catch {
    // noop
  }
}

// ── Request interceptor: attach Bearer token ─────────────────

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const stored = getPersistedAuth();
  if (stored.token && config.headers) {
    config.headers.Authorization = `Bearer ${stored.token}`;
  }
  // Inject org ID for multi-tenant API calls
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const orgId = parsed?.state?.org?.id;
      if (orgId && config.headers) {
        config.headers['x-org-id'] = orgId;
      }
    }
  } catch {
    // noop
  }
  return config;
});

// ── Response interceptor: refresh on 401 ─────────────────────

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token!);
    }
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle subscription expired (403 with SUBSCRIPTION_EXPIRED code)
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'SUBSCRIPTION_EXPIRED'
    ) {
      // Dynamic import to avoid circular dependency
      const { toast } = await import('react-hot-toast');
      toast.error('Tu periodo de prueba ha terminado. Suscribete para continuar.', {
        id: 'subscription-expired',
        duration: 5000,
      });
      return Promise.reject(error);
    }

    // Only attempt refresh for 401 and if we haven't retried already
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const { refreshToken } = getPersistedAuth();
    if (!refreshToken) {
      clearPersistedAuth();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request until the refresh completes
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((newToken) => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post<{ token: string; refreshToken: string }>(
        `${api.defaults.baseURL}/auth/refresh`,
        { refreshToken }
      );

      // Persist new tokens
      const stored = localStorage.getItem(STORE_KEY);
      if (stored) {
        const parsed: PersistedAuth = JSON.parse(stored);
        parsed.state.token = data.token;
        parsed.state.refreshToken = data.refreshToken;
        localStorage.setItem(STORE_KEY, JSON.stringify(parsed));
      }

      processQueue(null, data.token);

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
      }
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearPersistedAuth();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
